import { InformationCircle } from '~/generated/svg/icon/InformationCircle';
import { AiEmailGeneratorSpark11 } from '~/generated/svg/icon/streamline-ultimate/AiEmailGeneratorSpark11';
import { AppWindowMail4 } from '~/generated/svg/icon/streamline-ultimate/AppWindowMail4';
import { CalendarRemove7 } from '~/generated/svg/icon/streamline-ultimate/CalendarRemove7';
import { CommonFileDownload3 } from '~/generated/svg/icon/streamline-ultimate/CommonFileDownload3';
import { DeskDocumentBaseWork } from '~/generated/svg/icon/streamline-ultimate/DeskDocumentBaseWork';
import { DislikeHeartCancel } from '~/generated/svg/icon/streamline-ultimate/DislikeHeartCancel';
import { PhoneActionsNext6 } from '~/generated/svg/icon/streamline-ultimate/PhoneActionsNext6';
import { PhoneActionsSmile5 } from '~/generated/svg/icon/streamline-ultimate/PhoneActionsSmile5';
import { SingleNeutralActionsMail8 } from '~/generated/svg/icon/streamline-ultimate/SingleNeutralActionsMail8';
import { SingleNeutralActionsMoney3 } from '~/generated/svg/icon/streamline-ultimate/SingleNeutralActionsMoney3';
import {
  Phone,
  Mail,
  FileText,
  Calendar,
  CheckSquare,
  Eye,
  Images,
  Compass,
  Download,
  MessageSquare,
} from 'lucide-react';
import type { Dictionary } from '~/shared/definitions/Dictionary';

const classNamesBlue = 'bg-primary-50 dark:bg-primary-900/20';
const classNamesGreen = 'bg-secondary-50 dark:bg-secondary-900/20';
const classNamesDefault = 'bg-gray-100 dark:bg-gray-700';

export const cockpitActivityDesignDataConfig: Dictionary<DesignDataType> = {
  default: {
    icon: InformationCircle,
    classNames: classNamesDefault,
  },
  // Legacy MongoDB types
  agent_phone_seller: {
    icon: PhoneActionsSmile5,
    classNames: classNamesGreen,
  },
  mail_to_seller: {
    icon: SingleNeutralActionsMail8,
    classNames: classNamesGreen,
  },
  agent_phone_buyer: {
    icon: PhoneActionsNext6,
    classNames: classNamesBlue,
  },
  mail_to_buyer: {
    icon: AiEmailGeneratorSpark11,
    classNames: classNamesBlue,
  },
  lost_interest: {
    icon: DislikeHeartCancel,
    classNames: classNamesBlue,
  },
  mail_from_buyer_website: {
    icon: AppWindowMail4,
    classNames: classNamesGreen,
  },
  visit_slot_cancelled: {
    icon: CalendarRemove7,
    classNames: classNamesBlue,
  },
  offer_given: {
    icon: SingleNeutralActionsMoney3,
    classNames: classNamesGreen,
  },
  property_expose_download: {
    icon: CommonFileDownload3,
    classNames: classNamesGreen,
  },
  agent_admin_work: {
    icon: DeskDocumentBaseWork,
    classNames: classNamesBlue,
  },

  // Canonical types from unified-api / HubSpot engagements
  call: {
    icon: Phone,
    classNames: classNamesBlue,
  },
  email: {
    icon: Mail,
    classNames: classNamesBlue,
  },
  note: {
    icon: FileText,
    classNames: classNamesBlue,
  },
  meeting: {
    icon: Calendar,
    classNames: classNamesGreen,
  },
  task: {
    icon: CheckSquare,
    classNames: classNamesBlue,
  },

  // Strapi micro-activities
  page_view: {
    icon: Eye,
    classNames: classNamesBlue,
  },
  view: {
    icon: Eye,
    classNames: classNamesBlue,
  },
  slideshow: {
    icon: Images,
    classNames: classNamesGreen,
  },
  tour_360: {
    icon: Compass,
    classNames: classNamesGreen,
  },
  expose_download: {
    icon: Download,
    classNames: classNamesGreen,
  },
  inquiry: {
    icon: MessageSquare,
    classNames: classNamesGreen,
  },
  contact: {
    icon: MessageSquare,
    classNames: classNamesGreen,
  },
  visit: {
    icon: Calendar,
    classNames: classNamesGreen,
  },
  offer: {
    icon: SingleNeutralActionsMoney3,
    classNames: classNamesGreen,
  },
};

export const COCKPIT_ACTIVITY_TYPE_LABELS: Record<string, string> = {
  // HubSpot engagements
  call: 'Anruf',
  email: 'E-Mail',
  note: 'Notiz',
  meeting: 'Besichtigung / Termin',
  task: 'Aufgabe',

  // Strapi micro-activities
  page_view: 'Exposé aufgerufen',
  view: 'Exposé aufgerufen',
  slideshow: 'Bildergalerie aufgerufen',
  tour_360: '360°-Rundgang aufgerufen',
  expose_download: 'Exposé Download',
  property_expose_download: 'Exposé Download',
  inquiry: 'Kontaktanfrage',
  contact: 'Kontaktanfrage',
  visit: 'Besichtigung gebucht',
  visit_slot_cancelled: 'Termin abgesagt',
  offer: 'Angebot eingegangen',
  offer_given: 'Angebot abgegeben',

  // Legacy MongoDB types
  agent_phone_seller: 'Anruf',
  agent_phone_buyer: 'Anruf',
  mail_to_seller: 'Mail an Verkäufer',
  mail_to_buyer: 'Mail',
  mail_from_buyer_website: 'Mail',
  lost_interest: 'Interesse verloren',
  agent_admin_work: 'Vermarktung',
  property_shared: 'Objekt geteilt',
  document_requested: 'Dokumenteneinsicht angefragt',
  follow_up_contact: 'Nachfasskontakt',
  default: 'Aktivität',
};

export function getCockpitActivityTitle(
  t: (key: string, defaultValue?: string) => string,
  type?: string,
): string {
  const normalizedType = type || 'default';
  const defaultLabel =
    COCKPIT_ACTIVITY_TYPE_LABELS[normalizedType] ||
    COCKPIT_ACTIVITY_TYPE_LABELS.default;

  const titleResolvers: Record<string, () => string> = {
    page_view: () =>
      t('cockpit_activities_view_type_title_view', 'Exposé aufgerufen'),
    expose_download: () =>
      t(
        'cockpit_activities_view_type_title_property_expose_download',
        'Exposé Download',
      ),
    inquiry: () =>
      t('cockpit_activities_view_type_title_contact', 'Kontaktanfrage'),
    slideshow: () =>
      t(
        'cockpit_activities_view_type_title_slideshow',
        'Bildergalerie aufgerufen',
      ),
    tour_360: () =>
      t(
        'cockpit_activities_view_type_title_tour_360',
        '360°-Rundgang aufgerufen',
      ),
    call: () => t('cockpit_activities_view_type_title_call', 'Anruf'),
    email: () => t('cockpit_activities_view_type_title_email', 'E-Mail'),
    note: () => t('cockpit_activities_view_type_title_note', 'Notiz'),
    meeting: () =>
      t('cockpit_activities_view_type_title_meeting', 'Besichtigung / Termin'),
    task: () => t('cockpit_activities_view_type_title_task', 'Aufgabe'),
    visit: () =>
      t('cockpit_activities_view_type_title_visit', 'Besichtigung gebucht'),
    default: () => t('cockpit_activities_view_type_title_default', 'Aktivität'),
  };
  const aliases: Record<string, string> = {
    page_view: 'view',
    expose_download: 'property_expose_download',
    inquiry: 'contact',
  };
  const key = `cockpit_activities_view_type_title_${
    aliases[normalizedType] || normalizedType
  }`;
  const translated = titleResolvers[normalizedType]
    ? titleResolvers[normalizedType]()
    : t(key, defaultLabel);
  if (translated.trim() && translated !== key) {
    return translated;
  }
  return defaultLabel;
}

export function getCockpitActivityActor(
  t: (key: string, defaultValue?: string) => string,
  type: string | undefined,
  actor: string | undefined,
): string {
  const name = actor?.trim();
  if (name && !/^micro activity:/i.test(name) && !/^unknown$/i.test(name)) {
    return name;
  }
  const isMicroActivity = [
    'page_view',
    'view',
    'slideshow',
    'tour_360',
    'expose_download',
    'property_expose_download',
  ].includes(type || '');
  const key = isMicroActivity
    ? 'cockpit_activities_actor_interested'
    : 'cockpit_activities_actor_unknown';
  const fallback = isMicroActivity ? 'Interessent' : 'Unbekannter Kontakt';
  const translated = isMicroActivity
    ? t('cockpit_activities_actor_interested', 'Interessent')
    : t('cockpit_activities_actor_unknown', 'Unbekannter Kontakt');
  return translated.trim() && translated !== key ? translated : fallback;
}

interface DesignDataType {
  readonly icon: React.ComponentType<{ className?: string }>;
  readonly classNames: string;
}
